import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Pencil,
  Archive,
  ArchiveRestore,
  Trash2,
  Download,
  MoreHorizontal,
} from 'lucide-react';
import { TableSkeletonRows, TableEmptyRow } from '@/components/ui/table-states';
import { useToast } from '@/hooks/use-toast';
import { Matter } from '@/lib/store';
import { useStore } from '@/lib/store';
import { useUIConfig } from '@/config/ui-config';
import { useHideAmounts } from '@/hooks/useHideAmounts';
import { formatCurrency } from '@/utils/document';
import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';

interface MattersListProps {
  matters: Matter[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showInactive: boolean;
  onShowInactiveChange: (show: boolean) => void;
  onEdit: (matter: Matter) => void;
  onRefresh: () => void;
  isLoading: boolean;
  firmId?: string;
  onViewDetail?: (matter: Matter) => void;
}

export function MattersList({
  matters,
  searchQuery,
  onSearchChange,
  showInactive,
  onShowInactiveChange,
  onEdit,
  onRefresh,
  isLoading,
  firmId,
  onViewDetail
}: MattersListProps) {
  const { clients } = useStore();
  const { toast } = useToast();
  const { matter: matterConfig } = useUIConfig(firmId);
  const { hideAmounts } = useHideAmounts();

  const handleDelete = async (id: number) => {
    if (!matterConfig.confirmDelete || confirm('Are you sure you want to delete this matter?')) {
      try {
        await api.delete(`${ROUTES.API.MATTERS}${id}/`);
        toast({
          title: 'Deleted',
          description: 'Matter deleted successfully',
        });
        onRefresh();
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to delete matter',
          variant: 'destructive',
        });
      }
    }
  };

  const handleArchive = async (id: number) => {
    if (!matterConfig.confirmArchive || confirm('Are you sure you want to archive this matter?')) {
      try {
        await api.patch(`${ROUTES.API.MATTERS}${id}/`, { status: 'inactive' });
        toast({
          title: 'Archived',
          description: 'Matter archived successfully'
        });
        onRefresh();
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to archive matter',
          variant: 'destructive',
        });
      }
    }
  };

  const handleUnarchive = async (id: number) => {
    if (!matterConfig.confirmUnarchive || confirm('Are you sure you want to unarchive this matter?')) {
      try {
        await api.patch(`${ROUTES.API.MATTERS}${id}/`, { status: 'active' });
        toast({
          title: 'Unarchived',
          description: 'Matter unarchived successfully'
        });
        onRefresh();
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to unarchive matter',
          variant: 'destructive',
        });
      }
    }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      const endpoint = format === 'pdf'
        ? `${ROUTES.API.MATTERS}export_pdf/`
        : `${ROUTES.API.MATTERS}export_excel/`;

      const response = await api.get(endpoint, {
        responseType: 'blob',
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `matters.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: 'Success',
        description: `Matters exported as ${format.toUpperCase()} successfully`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Error',
        description: `Failed to export matters as ${format.toUpperCase()}`,
        variant: 'destructive',
      });
    }
  };

  const filteredMatters = matters
    .filter((matter) => {
      const searchLower = searchQuery.toLowerCase();
      if (!showInactive && matter.status === 'inactive') {
        return false;
      }

      if (
        matter.name.toLowerCase().includes(searchLower) ||
        matter.group_name?.toLowerCase().includes(searchLower)
      ) {
        return true;
      }

      if (
        matter.budget?.toString().includes(searchLower) ||
        matter.rounding_amount?.toString().includes(searchLower)
      ) {
        return true;
      }

      // Search in fee sharing participants (including client_purchase_id)
      const feeSharingSearchText = matter.fee_sharing_configs
        ?.flatMap((cfg) => cfg.participants?.map((p) => {
          const c = clients.find((x) => x.id === p.client);
          return [c?.name || '', p.client_purchase_id || ''].join(' ');
        }) || [])
        .join(' ') || '';

      if (feeSharingSearchText.toLowerCase().includes(searchLower)) {
        return true;
      }

      return false;
    })
    .sort((a, b) => {
      // Sort by group name first (nulls/empty at the end)
      const groupA = (a.group_name || '').toLowerCase();
      const groupB = (b.group_name || '').toLowerCase();

      if (!groupA && groupB) return 1;
      if (groupA && !groupB) return -1;

      if (groupA !== groupB) {
        return groupA.localeCompare(groupB);
      }

      // Then sort by matter name
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const getMatterDisplayName = (matter: Matter) => {
    if (matterConfig.showGroupNameInTitle && matter.group_name) {
      return `${matter.group_name} - ${matter.name}`;
    }
    return matter.name;
  };

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <SearchInput
          placeholder="Search matters..."
          value={searchQuery}
          onChange={onSearchChange}
          className="min-w-64 flex-1"
        />

        {(matterConfig.enablePdfExport || matterConfig.enableExcelExport) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {matterConfig.enablePdfExport && (
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  Export as PDF
                </DropdownMenuItem>
              )}
              {matterConfig.enableExcelExport && (
                <DropdownMenuItem onClick={() => handleExport('excel')}>
                  Export as Excel
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="flex items-center space-x-2">
          <Label htmlFor="show-inactive" className="text-sm font-medium">
            Show Inactive
          </Label>
          <Switch
            id="show-inactive"
            checked={showInactive}
            onCheckedChange={onShowInactiveChange}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Matter</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Clients</TableHead>
              <TableHead className="text-right">Budget</TableHead>
              <TableHead className="w-16 text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows colSpan={5} />
            ) : filteredMatters.length === 0 ? (
              <TableEmptyRow colSpan={5}>
                {searchQuery
                  ? 'No matters match your search.'
                  : 'No matters yet. Add a matter to get started.'}
              </TableEmptyRow>
            ) : (
              filteredMatters.map((matter) => (
                <TableRow
                  key={matter.id}
                  className={onViewDetail ? 'cursor-pointer' : ''}
                  onClick={() => onViewDetail?.(matter)}
                >
                  <TableCell>
                    <div>
                      <div className="font-medium">{getMatterDisplayName(matter)}</div>
                      {matter.group_name && !matterConfig.showGroupNameInTitle && (
                        <div className="text-sm text-muted-foreground">{matter.group_name}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={matter.status}
                      type="matter-status"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {(() => {
                        // Show participants from the active fee sharing config
                        const activeConfig = matter.fee_sharing_configs?.find(c => c.is_active)
                          || matter.fee_sharing_configs?.[0];
                        if (activeConfig?.participants?.length) {
                          return activeConfig.participants.map((p, idx) => {
                            const client = clients.find(c => c.id === p.client);
                            return client ? (
                              <div key={idx} className="text-sm flex items-center gap-1.5">
                                <span className="truncate">{client.name}</span>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {p.fee_sharing_type === 'percentage'
                                    ? `${p.percentage_share}%`
                                    : hideAmounts ? '•••' : formatCurrency(p.fixed_amount)}
                                </span>
                              </div>
                            ) : null;
                          });
                        }
                        return <span className="text-sm text-muted-foreground">—</span>;
                      })()}
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap tabular-nums">
                    <div>{matter.budget ? (hideAmounts ? '•••' : formatCurrency(matter.budget)) : '—'}</div>
                    {matter.phases && matter.phases.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {matter.phases.length} phase{matter.phases.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" title="Matter actions">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(matter)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit matter
                        </DropdownMenuItem>
                        {matter.status === 'active' ? (
                          <DropdownMenuItem onClick={() => handleArchive(matter.id)}>
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleUnarchive(matter.id)}>
                            <ArchiveRestore className="h-4 w-4 mr-2" />
                            Unarchive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(matter.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
